import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { logout } from "@/auth/authSlice";
import { getTenantBranding, type TenantBranding } from "@/services/tenantBrandingService";

const CACHE_TTL_MS = 15 * 60 * 1000;
const STORAGE_PREFIX = "tenant-branding-cache:";

type TenantBrandingCacheEntry = {
  branding: TenantBranding;
  loadedAt: string;
  expiresAt: string;
};

type TenantBrandingState = {
  currentTenantId: string | null;
  current: TenantBranding | null;
  cache: Record<string, TenantBrandingCacheEntry>;
  loading: boolean;
  error: string | null;
  /** Tenant a load most recently failed for — blocks TenantBrandingBootstrap's effect from
   *  re-dispatching every render (its guard only skips on a fresh cache hit, so without
   *  this a rejected load never satisfies that guard and gets retried in an unbounded
   *  render loop). Cleared on a successful load, or when a different tenant is loaded.
   *  Mirrors brandKitSlice.ts's failedTenantId exactly. */
  failedTenantId: string | null;
};

const initialState: TenantBrandingState = {
  currentTenantId: null,
  current: null,
  cache: {},
  loading: false,
  error: null,
  failedTenantId: null,
};

function isFresh(expiresAt: string) {
  return Date.parse(expiresAt) > Date.now();
}

function makeCacheEntry(branding: TenantBranding): TenantBrandingCacheEntry {
  const loadedAt = new Date().toISOString();
  const expiresAt = new Date(Date.now() + CACHE_TTL_MS).toISOString();
  return { branding, loadedAt, expiresAt };
}

function readStorage(tenantId: string): TenantBrandingCacheEntry | null {
  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}${tenantId}`);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as TenantBrandingCacheEntry;
    return parsed?.branding?.tenantId === tenantId ? parsed : null;
  } catch {
    return null;
  }
}

function writeStorage(entry: TenantBrandingCacheEntry) {
  try {
    window.localStorage.setItem(
      `${STORAGE_PREFIX}${entry.branding.tenantId}`,
      JSON.stringify(entry),
    );
  } catch {
    // Ignore storage failures.
  }
}

export const loadTenantBrandingAsync = createAsyncThunk(
  "tenantBranding/load",
  async (
    { tenantId, force = false }: { tenantId: string; force?: boolean },
    { getState, rejectWithValue },
  ) => {
    const state = getState() as { tenantBranding: TenantBrandingState };
    const cached = state.tenantBranding.cache[tenantId];

    if (!force && cached && isFresh(cached.expiresAt)) {
      return { tenantId, entry: cached, fromCache: true };
    }

    if (!force) {
      const stored = readStorage(tenantId);
      if (stored && isFresh(stored.expiresAt)) {
        return { tenantId, entry: stored, fromCache: true };
      }
    }

    try {
      const branding = await getTenantBranding();
      const entry = makeCacheEntry(branding);
      writeStorage(entry);
      return { tenantId, entry, fromCache: false };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to load tenant branding.",
      );
    }
  },
);

const tenantBrandingSlice = createSlice({
  name: "tenantBranding",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadTenantBrandingAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadTenantBrandingAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.failedTenantId = null;
        state.currentTenantId = action.payload.tenantId;
        state.current = action.payload.entry.branding;
        state.cache[action.payload.tenantId] = action.payload.entry;
      })
      .addCase(loadTenantBrandingAsync.rejected, (state, action) => {
        state.loading = false;
        state.failedTenantId = action.meta.arg.tenantId;
        state.error =
          typeof action.payload === "string"
            ? action.payload
            : (action.error.message ?? "Failed to load tenant branding.");
      })
      .addCase(logout, () => initialState);
  },
});

export default tenantBrandingSlice.reducer;
